import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Odometer } from "@/components/Odometer";
import { RevealHeading } from "@/components/RevealHeading";
import { MotionInView } from "@/components/MotionInView";
import { services } from "@/content/services";
import { industries } from "@/content/industries";
import { company, stats } from "@/content/site";

/** A few representative products, pulled from the industries we serve. */
const products = industries.flatMap((i) => i.examples).slice(0, 12);

export default function Services() {
  return (
    <>
      <Seo
        title={`Services | ${company.name}`}
        description="Liquid bulk and HazMat tanker hauling, plus dry van and reefer support, dedicated lanes, expedited loads, and broker partnerships - across all 48 states and into Canada."
      />

      {/* HERO (photo + scrim, KAG-style) */}
      <section className="relative overflow-hidden bg-brand-900 text-white">
        <img
          src="/brand/industries/chemicals.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-900 via-brand-900/85 to-brand-900/40" />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 10px)" }}
        />
        <div className="relative mx-auto max-w-[1200px] px-6 py-20 lg:py-28">
          <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">Services</div>
          <RevealHeading
            as="h1"
            text="Tanker capacity, built around your freight."
            className="mb-5 max-w-3xl text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.05]"
          />
          <p className="max-w-2xl text-lg text-brand-200">
            From a single dedicated lane to time-critical loads, {company.shortName}{" "}
            runs the equipment, endorsements, and dispatch to move liquid bulk
            freight right - and keep you in the loop the whole way. Tanker is our
            core, with dry van and reefer support available on select lanes.
          </p>
        </div>
      </section>

      {/* INTRO + STATS (Quality "Work with the Leader") */}
      <section className="border-b border-brand-100 bg-brand-50 py-12">
        <div className="mx-auto grid max-w-[1200px] items-center gap-8 px-6 lg:grid-cols-[1.4fr_1fr]">
          <p className="text-xl font-semibold leading-snug text-brand-900 lg:text-2xl">
            One carrier you can build a freight program around - modern equipment,
            professional drivers, and visibility on every load.
          </p>
          <div className="grid gap-4 sm:grid-cols-3 lg:gap-6 lg:border-l lg:border-brand-200 lg:pl-10">
            {[
              { v: stats.years, l: "Years moving freight" },
              { v: stats.lanes, l: "States covered" },
              { v: stats.onTime, l: "On-time delivery" },
            ].map((s) => (
              <div key={s.l}>
                <div className="text-3xl font-bold text-accent-500 lg:text-4xl">
                  <Odometer value={s.v} />
                </div>
                <div className="mt-1 text-xs text-brand-500 lg:text-sm">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID (bold dark tiles) */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal className="mb-12 max-w-2xl">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">What we offer</div>
            <RevealHeading text="Find your freight solution." className="text-4xl lg:text-5xl" />
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <MotionInView key={s.slug} delay={(i % 3) * 0.1} className="h-full">
                  <Link
                    to={`/services/${s.slug}`}
                    className="group flex h-full flex-col rounded-lg border border-brand-100 bg-white p-6 transition-all hover:-translate-y-1 hover:border-accent-500 hover:shadow-lg"
                  >
                    <div className="mb-4 flex items-start justify-between">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-brand-900 text-white transition-colors group-hover:bg-accent-500">
                        <Icon className="h-5 w-5" />
                      </div>
                      <ArrowRight className="h-5 w-5 text-brand-300 transition-all group-hover:translate-x-1 group-hover:text-accent-500" />
                    </div>
                    <h3 className="mb-2 text-lg">{s.title}</h3>
                    <p className="mb-4 text-sm text-brand-500">{s.summary}</p>
                    <span className="mt-auto inline-flex items-center text-sm font-semibold text-accent-500">
                      Learn more <ChevronRight className="ml-0.5 h-4 w-4" />
                    </span>
                  </Link>
                </MotionInView>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRODUCTS / INDUSTRIES (Quality "Products We Move") */}
      <section className="bg-brand-900 py-20 text-white lg:py-24">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 lg:grid-cols-2">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">Products we move</div>
            <RevealHeading text="If it ships in a tanker, we haul it." className="mb-5 text-3xl lg:text-4xl" />
            <p className="mb-7 max-w-lg text-lg text-brand-200">
              Across chemicals, lubricants, agriculture, and
              manufacturing - the right equipment for each load.
            </p>
            <Link to="/industries" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
              Industries we serve <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={120}>
            <ul className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {products.map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm text-brand-100">
                  <Check className="h-4 w-4 shrink-0 text-accent-400" /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <Reveal className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-6 px-6 md:flex-row">
          <div>
            <RevealHeading text="Get in touch." className="text-3xl text-brand-900 lg:text-4xl" />
            <p className="mt-2 max-w-xl text-brand-500">
              Tell us your lane, product, and timing - we'll get you a quote and the
              right equipment.
            </p>
          </div>
          <div className="flex w-full shrink-0 flex-wrap gap-3 md:w-auto">
            <Link to="/contact" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
              Get a quote
            </Link>
            <Link
              to="/track"
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "w-full justify-center border-brand-900 text-brand-900 hover:bg-brand-900 hover:text-white sm:w-auto",
              )}
            >
              Track a shipment
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
